/**
 * Utility to calculate overall job score based on various metrics
 * Enhanced with memoization for improved performance
 */
import { calculationCache } from './cacheManager';
import { performanceMonitor } from './performanceMonitor';

// Weights for company overall (0–10). Referral is included; remaining five
// are scaled from the prior 100% so the six dimensions still sum to 1.0.
const SCORE_WEIGHTS = {
    compensation: 0.225,
    growth: 0.18,
    stability: 0.135,
    WLB: 0.18,
    toxicity: 0.18,
    referral: 0.10
};

/**
 * Normalize company scores from database format to expected frontend format
 * Handles multiple formats:
 * - Old format: "Compensation", "Stability", "Growth Potential", "Work-life Balance", "Culture"
 * - New backend format: compensation, stability, careerGrowth, workLifeBalance, toxicity
 * - Nested format: { compensation: { weightedScore: 75 } }
 * 
 * @param {Object} rawScores - Raw scores object from database
 * @returns {Object} - Normalized scores object
 */
export const normalizeCompanyScores = (rawScores) => {
    if (!rawScores || typeof rawScores !== 'object') {
        return {};
    }

    const normalized = {};

    // Helper to extract score value (handles nested weightedScore format)
    const extractValue = (value) => {
        if (typeof value === 'number') return value;
        if (value && typeof value === 'object' && typeof value.weightedScore === 'number') {
            return value.weightedScore;
        }
        return null;
    };

    // Map all possible field name variations to standard format
    const fieldMappings = {
        // Compensation
        'compensation': 'compensation',
        'Compensation': 'compensation',
        'COMPENSATION': 'compensation',
        
        // Growth/Career Growth
        'growth': 'growth',
        'Growth': 'growth',
        'careerGrowth': 'growth',
        'Growth Potential': 'growth',
        'CAREER_GROWTH': 'growth',
        
        // Stability
        'stability': 'stability',
        'Stability': 'stability',
        'STABILITY': 'stability',
        
        // Work-Life Balance
        'WLB': 'WLB',
        'wlb': 'WLB',
        'workLifeBalance': 'WLB',
        'Work-life Balance': 'WLB',
        'Work Life Balance': 'WLB',
        'WORK_LIFE_BALANCE': 'WLB',
        
        // Toxicity/Culture
        'toxicity': 'toxicity',
        'Toxicity': 'toxicity',
        'TOXICITY': 'toxicity',
        'Culture': 'toxicity',  // Old format used "Culture" for toxicity
        'culture': 'toxicity',

        // Referral (network) opportunity
        'referral': 'referral',
        'Referral': 'referral',
        'REFERRAL': 'referral'
    };

    // Process each field in raw scores
    for (const [rawKey, value] of Object.entries(rawScores)) {
        const normalizedKey = fieldMappings[rawKey];
        if (normalizedKey) {
            const extractedValue = extractValue(value);
            if (extractedValue !== null && !isNaN(extractedValue)) {
                normalized[normalizedKey] = extractedValue;
            }
        }
    }

    return normalized;
};

/**
 * Calculate the overall job score from individual metrics
 * @param {Object} scores - Object containing individual scores
 * @param {number} scores.compensation - Compensation score (0-100)
 * @param {number} scores.growth - Career growth score (0-100)
 * @param {number} scores.stability - Stability score (0-100)
 * @param {number} scores.WLB - Work-life balance score (0-100)
 * @param {number} scores.toxicity - Toxicity score (0-100, lower is better)
 * @param {number} [scores.referral] - Referral opportunity (0-100, higher is better)
 * @returns {Object} - Returns object with score (0-10 scale) and status
 */
// Internal function removed - not needed

/**
 * Calculate the overall job score from individual metrics - memoized version
 * @param {Object} scores - Object containing individual scores (in any format)
 * @returns {Object} - Returns object with score (0-10 scale) and status
 */
export const calculateOverallJobScore = (scores) => {
    // Use the performance monitor to measure this calculation
    return performanceMonitor.measure(() => {
        // If any required score is missing, return null
        if (!scores || typeof scores !== 'object') {
            return { score: null, status: 'missing' };
        }

        // Normalize scores from various database formats
        const normalizedScores = normalizeCompanyScores(scores);
        
        // Generate cache key from the normalized scores
        const cacheKey = `jobScore:${JSON.stringify(normalizedScores)}`;
        
        // Check cache first
        const cached = calculationCache.get(cacheKey);
        if (cached) {
            return cached;
        }
        
        // Check if we have enough valid scores to calculate
        const validScores = Object.keys(SCORE_WEIGHTS).filter(key => 
            typeof normalizedScores[key] === 'number' && !isNaN(normalizedScores[key]));
        
        // We need at least 3 valid scores to calculate an overall score
        if (validScores.length < 3) {
            return { score: null, status: 'insufficient' };
        }
        
        // Calculate weighted average (note: toxicity score needs to be inverted)
        let totalWeight = 0;
        let weightedSum = 0;
        
        for (const key of validScores) {
            const weight = SCORE_WEIGHTS[key];
            let value = normalizedScores[key];
            // Toxicity is already stored as Culture Health (100 is good, 0 is bad)
            // No need to invert the value
            
            weightedSum += value * weight;
            totalWeight += weight;
        }
    
        // If we don't have all metrics, normalize by the total weight we do have
        const normalizedScore = weightedSum / totalWeight;
        
        // Convert to 0-10 scale and round to 1 decimal
        const score = Math.round(normalizedScore / 10 * 10) / 10;
        
        const result = { score, status: 'calculated' };
        
        // Cache the result
        calculationCache.set(cacheKey, result);
        
        return result;
    }, 'calculateOverallJobScore', 'jobScores');
};

/**
 * Format the job score for display
 * @param {Object} scoreResult - The result from calculateOverallJobScore
 * @param {number} scoreResult.score - The calculated score
 * @param {string} scoreResult.status - The status of the calculation
 * @returns {string} - Formatted score string (e.g. "8.5/10")
 */
export const formatJobScore = (scoreResult) => {
    // Simple function, using regular memoization through the cache manager
    const cacheKey = `formatJobScore:${JSON.stringify(scoreResult)}`;
    const cached = calculationCache.get(cacheKey);
    
    if (cached) {
        return cached;
    }
    
    let result;
    if (!scoreResult || scoreResult.status === 'missing' || scoreResult.score === null) {
        result = 'N/A';
    } else {
        result = `${scoreResult.score}/10`;
    }
    
    calculationCache.set(cacheKey, result);
    return result;
};

/**
 * Get the color class for a score based on its value
 * @param {number} score - The score value (0-10)
 * @returns {string} - Tailwind CSS color class
 */
export const getScoreColorClass = (score) => {
    // Use the cache manager's memoize function for simple functions
    return calculationCache.memoize((score) => {
        if (score === null || score === undefined) return 'text-gray-400';
        
        if (score >= 8) return 'text-green-600';
        if (score >= 6) return 'text-blue-500';
        if (score >= 4) return 'text-yellow-500';
        return 'text-red-500';
    })(score);
};

/**
 * Get the background color class for a score badge
 * @param {number} score - The score value (0-10) 
 * @returns {string} - Tailwind CSS background color class
 */
export const getScoreBgColorClass = (score) => {
    // Use the cache manager's memoize function for simple functions
    return calculationCache.memoize((score) => {
        if (score === null || score === undefined) return 'bg-gray-400';
        
        if (score >= 8) return 'bg-green-600';
        if (score >= 6) return 'bg-blue-500';
        if (score >= 4) return 'bg-yellow-500';
        return 'bg-red-500';
    })(score);
};

const jobScoreCalculator = {
    calculateOverallJobScore,
    formatJobScore,
    getScoreColorClass,
    getScoreBgColorClass,
    normalizeCompanyScores
};

export default jobScoreCalculator;
